import React, { FunctionComponent, useState } from "react";
import { Node, useReactFlow } from "reactflow";
import {
  BannerConsumer,
  BannerPlaceholder,
  BannerProvider,
  IBannerContext,
  BannerStatus,
  RequiredState,
  Dialog,
  Form,
  TextInput,
  NumericInput,
  Button,
  InputSize,
  Tree,
  ValidationMessage
} from "@plex/react-components";

import { FlowInputManagementController, IFlowInputProperties } from "./FlowInputManagementController";
import "./FlowInputManagement.scss";
import { getSchema } from "../NodeTypes/DataSchemas";
import {
  DocumentSchemaType,
  IDocumentListSchema,
  IDocumentObjectSchema,
  IDocumentObjectSchemaProperty,
  IDocumentSchema,
  isCompositeIdSchema,
  isReferenceSchema,
  propertyIsIdentifier
} from "../FlowDocument/FlowDocumentModel";
import { camelCaseToReadable } from "../Util/NodeUtil";
import { IFlowInputEditorOptions, INodeProperty } from "../NodeTypes/Base";
import { NodeConfigPropertyType } from "../FlowDocument/PropertyTypeDefinitions";
import { getFlowInputName } from "../NodeTypes/SchemaSystem";
import { useViewController } from "../ViewContext";

export interface IFlowInputDataEditorParams {
  show: boolean;
  onClose: () => void;
}

export interface IFlowInputDataEditorState {
  show: boolean;
  title?: string;
  inputName?: string;
  excludedProperties: string[];
  testData: { [inputName: string]: { [propertyName: string]: string | boolean | number | any } };
}

export const FlowInputDataEditor: FunctionComponent<IFlowInputDataEditorParams> = (props) => {
  const flowInputDataEditorState = useViewController().flowInputDataEditorState;
  const { show, onClose } = props;
  return (
    <Dialog show={show} title={flowInputDataEditorState.title ?? "Flow Inputs"} onHide={onClose}>
      <div style={{ height: "auto" }}>
        <FlowInputDataEditorForm />
      </div>
    </Dialog>
  );
};

interface IValuePath {
  pathProperty: string;
  isList?: boolean;
  isPrimitive?: boolean;
  listIndex?: number;
  listItemIsObject?: boolean;
}

interface INamedDocumentObjectSchemaProperty extends IDocumentObjectSchemaProperty {
  name: string;
  // The property path to the value the editor is updating.
  valuePath: IValuePath[];
  isEmptyList?: boolean;
  errorMessage?: string;
}

type TestDataNode = {
  id: number;
  name: string;
  label: string;
  expanded: boolean;
  // The property path to this node.
  valuePath: IValuePath[];
  parentId?: number;
  schemaProperty?: INamedDocumentObjectSchemaProperty;
};

const getFlowInputLocalStorage = (flowInputController: FlowInputManagementController, flowInputName: string) => {
  const flowInput = flowInputController
    .getProperties()
    .find((property: IFlowInputProperties) => property.propertyName === flowInputName);
  if (flowInput?.propertyType) {
    const flowInputJsonString = localStorage.getItem(flowInput.propertyType);
    if (flowInputJsonString) {
      return JSON.parse(flowInputJsonString);
    }
  }

  return undefined;
};

const setFlowInputLocalStorage = (
  flowInputController: FlowInputManagementController,
  flowInputDataEditorState: IFlowInputDataEditorState,
  flowInputName: string
) => {
  const flowInput = flowInputController
    .getProperties()
    .find((property: IFlowInputProperties) => property.propertyName === flowInputName);
  if (flowInput?.propertyType) {
    const flowInputJsonString = JSON.stringify(flowInputDataEditorState.testData[flowInputName]);
    localStorage.setItem(flowInput.propertyType, flowInputJsonString);
  }
};

const loadOrInitFlowInput = (
  flowInputController: FlowInputManagementController,
  flowInputDataEditorState: IFlowInputDataEditorState,
  flowInputName: string,
  defaultValue: any
) => {
  if (!flowInputDataEditorState.testData[flowInputName]) {
    const localStorageFlowInput = getFlowInputLocalStorage(flowInputController, flowInputName);
    if (localStorageFlowInput) {
      flowInputDataEditorState.testData[flowInputName] = localStorageFlowInput;
    } else {
      flowInputDataEditorState.testData[flowInputName] = { ...defaultValue };
    }
  }
};

const initProperty = (
  inputName: string,
  flowInputController: FlowInputManagementController,
  flowInputDataEditorState: IFlowInputDataEditorState,
  propertyName: string,
  valuePath: IValuePath[],
  defaultValues: any
) => {
  loadOrInitFlowInput(flowInputController, flowInputDataEditorState, inputName, defaultValues);
  let currentPath = flowInputDataEditorState.testData[inputName]!;
  for (let i = 0; i < valuePath.length; i++) {
    if (
      currentPath[valuePath[i]?.pathProperty!] === undefined ||
      (i > 0 && valuePath[i - 1]?.isList && valuePath[i - 1]?.listIndex! > currentPath.length - 1)
    ) {
      if (valuePath[i]?.isList) {
        if (i > 0 && valuePath[i - 1]?.isList) {
          if (valuePath[i]?.listIndex! > currentPath.length - 1) {
            currentPath.push(null);
          }
          currentPath[valuePath[i]?.listIndex!] = [];
        } else {
          currentPath[valuePath[i]?.pathProperty!] = [];
        }
      } else if (!valuePath[i]?.isPrimitive) {
        currentPath[valuePath[i]?.pathProperty!] = {};
      }
    }
    if (i > 0 && valuePath[i - 1]?.isList) {
      currentPath = currentPath[valuePath[i]?.listIndex!];
    } else if (!valuePath[i]?.isPrimitive) {
      currentPath = currentPath[valuePath[i]?.pathProperty!];
    }
  }
  if (valuePath.length > 0 && !valuePath[valuePath.length - 2]?.isList && currentPath![propertyName] === undefined) {
    currentPath![propertyName] = null;
  }
};

const toLabel = (name: string) => camelCaseToReadable(name).replaceAll("_", " ");

const getPropertiesFromSchema = (
  schema: IDocumentSchema,
  data: any,
  parentValuePath: IValuePath[],
  excludedProperties: string[]
) => {
  let properties: INamedDocumentObjectSchemaProperty[] = [];

  const pushListProperties = (listSchema: IDocumentListSchema, isNestedProperty?: boolean, propertyName?: string) => {
    const getListData = () => {
      if (!data) {
        return undefined;
      }

      if (isNestedProperty) {
        if (data[propertyName!] && Array.isArray(data[propertyName!])) {
          return data[propertyName!];
        }
      } else if (Array.isArray(data)) {
        return data;
      }
    };
    const listData = getListData();
    const listCount = listData ? listData.length : 0;
    const listItemSchema = getSchema(listSchema.listItemSchema);
    if (listItemSchema) {
      if (listSchema.listItemSchema) {
        if (listCount === 0) {
          properties.push({
            name: propertyName ?? "",
            schema: listItemSchema.id,
            isEmptyList: true,
            required: false,
            valuePath: parentValuePath,
            label: "Empty"
          });
        }
        for (let i = 0; i < listCount; i++) {
          const itemName = `Item ${i + 1}`;
          if (listItemSchema.schemaType === DocumentSchemaType.primitive) {
            let newValuePath = parentValuePath;
            if (isNestedProperty) {
              newValuePath = newValuePath.concat({ pathProperty: propertyName!, isList: true });
            }
            newValuePath = newValuePath.concat([
              {
                pathProperty: i.toString(),
                listIndex: i,
                isPrimitive: listItemSchema.schemaType === DocumentSchemaType.primitive
              }
            ]);
            properties.push({
              name: i.toString(),
              schema: listItemSchema.id,
              required: false,
              valuePath: newValuePath,
              label: itemName
            });
          } else {
            let newListValuePath = parentValuePath;
            let listItemIsObject = false;
            if (listItemSchema.schemaType === DocumentSchemaType.list) {
              const listItemListSchema = getSchema((listItemSchema as unknown as IDocumentListSchema).listItemSchema);
              if (listItemListSchema && listItemListSchema.schemaType === DocumentSchemaType.object) {
                listItemIsObject = true;
              }
            }
            if (isNestedProperty) {
              newListValuePath = newListValuePath.concat({
                pathProperty: propertyName!,
                isList: true,
                listItemIsObject: listItemIsObject
              });
            }
            newListValuePath = newListValuePath.concat([
              {
                pathProperty: i.toString(),
                isList: listItemSchema.schemaType === DocumentSchemaType.list,
                listIndex: i,
                listItemIsObject: listItemIsObject
              }
            ]);

            let itemData: any;
            if (listData && i < listData.length) {
              itemData = listData[i];
            }

            properties = properties.concat(
              getPropertiesFromSchema(listItemSchema, itemData, newListValuePath, excludedProperties)
            );
          }
        }
      }
    }
  };

  const pushIdentifierProperties = (
    propertyName: string,
    property: IDocumentObjectSchemaProperty | undefined,
    propertySchema: IDocumentObjectSchema
  ) => {
    const identifierProperties: INamedDocumentObjectSchemaProperty[] = Object.keys(propertySchema.properties)
      .filter((referencePropertyName: string) => propertyIsIdentifier(propertySchema.properties[referencePropertyName]))
      .map((referencePropertyName: string) => {
        return {
          ...propertySchema.properties[referencePropertyName]!,
          name: referencePropertyName,
          valuePath: parentValuePath,
          label: toLabel(referencePropertyName)
        };
      });
    identifierProperties.forEach((identifierProperty: INamedDocumentObjectSchemaProperty) => {
      const identifierSchema = getSchema(identifierProperty.schema);
      if (isCompositeIdSchema(identifierSchema)) {
        const compositeIdentifierSchema = identifierSchema as unknown as IDocumentObjectSchema;
        if (compositeIdentifierSchema.properties) {
          Object.keys(compositeIdentifierSchema.properties).forEach((compositePropertyName: string) => {
            if (compositeIdentifierSchema.properties[compositePropertyName]) {
              properties.push({
                ...compositeIdentifierSchema.properties[compositePropertyName]!,
                name: compositePropertyName,
                required: property?.required ?? false,
                valuePath: parentValuePath.concat([
                  { pathProperty: propertyName },
                  { pathProperty: identifierProperty.name }
                ]),
                label:
                  compositeIdentifierSchema.properties[compositePropertyName]!.label ?? toLabel(compositePropertyName)
              });
            }
          });
        }
      } else {
        properties.push({
          ...identifierProperty!,
          required: property?.required ?? false,
          valuePath: parentValuePath.concat([{ pathProperty: propertyName }]),
          label: identifierProperty?.label
        });
      }
    });
  };

  if (schema.schemaType === DocumentSchemaType.list) {
    pushListProperties(schema as unknown as IDocumentListSchema);
  }

  const objectSchema = schema as unknown as IDocumentObjectSchema;
  if (objectSchema.properties) {
    Object.keys(objectSchema.properties)
      .filter(
        (propertyName: string) => !excludedProperties.includes(propertyName) && objectSchema.properties[propertyName]
      )
      .forEach((propertyName: string) => {
        const property = objectSchema.properties[propertyName];
        const propertySchema = getSchema(property?.schema ?? "") as unknown as IDocumentObjectSchema;
        if (propertySchema?.properties && isReferenceSchema(propertySchema)) {
          pushIdentifierProperties(propertyName, property, propertySchema);
        } else {
          if (propertySchema.schemaType === DocumentSchemaType.list) {
            pushListProperties(propertySchema as unknown as IDocumentListSchema, true, propertyName);
          } else if (propertySchema.schemaType === DocumentSchemaType.object) {
            let propertyData: any = undefined;
            if (data && typeof data === "object") {
              propertyData = data[propertyName];
            }
            properties = properties.concat(
              getPropertiesFromSchema(
                propertySchema,
                propertyData,
                parentValuePath.concat([{ pathProperty: propertyName }]),
                excludedProperties
              )
            );
          } else if (propertySchema.schemaType === DocumentSchemaType.primitive) {
            properties.push({
              ...property!,
              name: propertyName,
              valuePath: parentValuePath,
              label: objectSchema.properties[propertyName]?.label ?? toLabel(propertyName)
            });
          }
        }
      });
  }

  return properties;
};

const getFlowInputProperties = (
  flowInputName: string,
  excludedProperties: string[],
  flowInputController: FlowInputManagementController,
  flowInputDataEditorState: IFlowInputDataEditorState
) => {
  let properties: INamedDocumentObjectSchemaProperty[] = [];

  const flowInput = flowInputController
    .getProperties()
    .find((property: IFlowInputProperties) => property.propertyName === flowInputName);
  if (flowInput) {
    const schema = getSchema(flowInput.propertyType) as unknown as IDocumentObjectSchema;
    if (schema?.properties) {
      properties = properties.concat(
        getPropertiesFromSchema(
          schema,
          flowInputDataEditorState?.testData[flowInputDataEditorState?.inputName!],
          [],
          excludedProperties
        )
      );
    }
  }

  return properties;
};

export const initAllProperties = (
  nodes: Node<any>[],
  flowInputDataEditorState: IFlowInputDataEditorState,
  flowInputController: FlowInputManagementController
) => {
  nodes.forEach((node: Node<any>) => {
    const nodeDefinition = globalThis.nodeTypeDefinitions.getDefinition(node.type!);
    if (nodeDefinition) {
      const dataEditorProps = nodeDefinition.nodeConfigProperties.filter(
        (prop: INodeProperty) => prop.propertyType === NodeConfigPropertyType.FlowInputDataEditor
      );
      dataEditorProps.forEach((prop: INodeProperty) => {
        const options = prop.options as IFlowInputEditorOptions;
        if (options) {
          const flowInputName = getFlowInputName(node.id, options.inputSubName);
          loadOrInitFlowInput(flowInputController, flowInputDataEditorState, flowInputName, options.defaultValue);
          const flowInputProps = getFlowInputProperties(
            flowInputName,
            options.excludedProperties,
            flowInputController,
            flowInputDataEditorState
          );
          flowInputProps.forEach((flowInputProp: INamedDocumentObjectSchemaProperty) => {
            initProperty(
              flowInputName,
              flowInputController,
              flowInputDataEditorState,
              flowInputProp.name,
              flowInputProp.valuePath,
              options.defaultValue
            );
          });
          if (options.applyOverrides) {
            options.applyOverrides(node!, flowInputDataEditorState.testData[flowInputName]);
          }
        }
      });
    }
  });
};

const getFlowInputDataEditorProperties = (
  nodes: Node<any>[],
  forFlowInputName: string
): { node: Node<any>; prop: INodeProperty; options: IFlowInputEditorOptions }[] => {
  const matchingProperties: { node: Node<any>; prop: INodeProperty; options: IFlowInputEditorOptions }[] = [];

  nodes.forEach((node: Node<any>) => {
    const nodeDefinition = globalThis.nodeTypeDefinitions.getDefinition(node.type!);
    if (nodeDefinition) {
      const dataEditorProps = nodeDefinition.nodeConfigProperties.filter(
        (prop: INodeProperty) => prop.propertyType === NodeConfigPropertyType.FlowInputDataEditor
      );

      dataEditorProps.forEach((prop: INodeProperty) => {
        const options = prop.options as IFlowInputEditorOptions;
        if (options) {
          const flowInputName = getFlowInputName(node.id, options.inputSubName);
          if (flowInputName === forFlowInputName) {
            matchingProperties.push({ node, prop, options });
          }
        }
      });
    }
  });

  return matchingProperties;
};

const getValueAtPath = (valuePath: IValuePath[], rootObject: any) => {
  if (rootObject === null || rootObject === undefined) {
    return [undefined, undefined] as const;
  }
  let parentPath:
    | {
        [propertyName: string]: any;
      }
    | undefined = undefined;
  let currentPath = rootObject;
  for (let i = 0; i < valuePath.length; i++) {
    if (currentPath !== undefined) {
      parentPath = currentPath;
      if (i > 0 && valuePath[i - 1]?.isList) {
        currentPath = currentPath[valuePath[i]?.listIndex!];
      } else if (!valuePath[i]?.isPrimitive) {
        currentPath = currentPath[valuePath[i]?.pathProperty!];
      }
    }
  }
  return [currentPath, parentPath] as const;
};

const getDefaultValueForFlowInput = (flowInputName: string, reactFlowInstance: any): any => {
  const nodes = reactFlowInstance.getNodes();
  const matchingProperties = getFlowInputDataEditorProperties(nodes, flowInputName);

  if (matchingProperties.length > 0) {
    return matchingProperties[0]?.options?.defaultValue || {};
  }

  return {};
};

const validateRequiredProperties = (
  nodes: Node<any>[],
  flowInputDataEditorState: IFlowInputDataEditorState,
  flowInputController: FlowInputManagementController,
  forFlowInputName: string
): [string[], INamedDocumentObjectSchemaProperty[]] => {
  const missingRequiredInputs: string[] = [];
  let flowInputProps: INamedDocumentObjectSchemaProperty[] = [];

  const matchingProperties = getFlowInputDataEditorProperties(nodes, forFlowInputName);

  matchingProperties.forEach(({ node, prop, options }) => {
    const flowInputName = getFlowInputName(node.id, options.inputSubName);
    loadOrInitFlowInput(flowInputController, flowInputDataEditorState, flowInputName, options.defaultValue);
    flowInputProps = getFlowInputProperties(
      flowInputName,
      options.excludedProperties,
      flowInputController,
      flowInputDataEditorState
    );

    flowInputProps.forEach((flowInputProp: INamedDocumentObjectSchemaProperty) => {
      let inputValue: any = null;
      if (flowInputDataEditorState.testData[flowInputName]) {
        const [currentPath, _parentPath] = getValueAtPath(
          flowInputProp.valuePath,
          flowInputDataEditorState.testData[flowInputName]!
        );
        if (currentPath) {
          inputValue = currentPath[flowInputProp.name];
        }
        if (typeof inputValue === "string") {
          inputValue = (inputValue as string).trim();
        }
      }
      const flowInputHasValue = inputValue !== undefined && inputValue !== null && inputValue !== "";
      if (flowInputProp.required && !flowInputHasValue) {
        missingRequiredInputs.push(flowInputProp.label ?? toLabel(flowInputProp.name));
        flowInputProp.errorMessage = "Required";
      }
    });
  });

  return [missingRequiredInputs, flowInputProps];
};

const FlowInputDataEditorForm = (props: any) => {
  const reactFlowInstance = useReactFlow();
  const { flowInputController, setFlowInputDataEditorState, flowInputDataEditorState } = useViewController();
  const [validationState, setValidationState] = useState({ showing: false });

  const closeNotifyModal = (forFlowInputName: string, validate: boolean = false, bannerContext?: IBannerContext) => {
    let valid = true;
    if (validate) {
      const [missingInputs, _inputProps] = validateRequiredProperties(
        reactFlowInstance.getNodes(),
        flowInputDataEditorState,
        flowInputController,
        forFlowInputName
      );
      valid = missingInputs.length === 0;
      if (bannerContext && missingInputs.length > 0) {
        bannerContext.addMessage("Inputs are required: " + missingInputs.join(", "), BannerStatus.error);
        validationState.showing = true;
      } else {
        validationState.showing = false;
      }
      setValidationState({ ...validationState });
    }
    if (valid) {
      flowInputDataEditorState.testData;
      setFlowInputDataEditorState({ ...flowInputDataEditorState, show: false });
    }
  };

  const getCurrentValue = (property: INamedDocumentObjectSchemaProperty) => {
    const defaultValue = getDefaultValueForFlowInput(flowInputDataEditorState?.inputName!, reactFlowInstance);
    loadOrInitFlowInput(
      flowInputController,
      flowInputDataEditorState,
      flowInputDataEditorState?.inputName!,
      defaultValue
    );
    if (flowInputDataEditorState?.testData[flowInputDataEditorState?.inputName!] != null) {
      const [currentPath, parentPath] = getValueAtPath(
        property.valuePath,
        flowInputDataEditorState?.testData[flowInputDataEditorState?.inputName!]
      );

      if (
        parentPath !== undefined &&
        property.valuePath.length > 1 &&
        property.valuePath[property.valuePath.length - 2]?.isList
      ) {
        const lastValuePath = property.valuePath[property.valuePath.length - 1];
        if (parentPath![lastValuePath?.listIndex!] !== undefined) {
          if (lastValuePath?.isPrimitive) {
            return parentPath![lastValuePath?.listIndex!];
          } else {
            return parentPath![lastValuePath?.listIndex!][property.name];
          }
        }
      }

      if (currentPath !== undefined && currentPath[property.name] !== undefined) {
        return currentPath![property.name];
      }
    }

    return "";
  };

  const onChange = (propertyName: string, valuePath: IValuePath[], value: string | boolean | number | null) => {
    const defaultValue = getDefaultValueForFlowInput(flowInputDataEditorState?.inputName!, reactFlowInstance);
    initProperty(
      flowInputDataEditorState.inputName!,
      flowInputController,
      flowInputDataEditorState,
      propertyName,
      valuePath,
      defaultValue
    );
    const [currentPath, parentPath] = getValueAtPath(
      valuePath,
      flowInputDataEditorState?.testData[flowInputDataEditorState?.inputName!]
    );

    const parentValuePath = valuePath.length > 1 ? valuePath[valuePath.length - 2] : undefined;
    if (parentValuePath?.isList) {
      const lastValuePath = valuePath[valuePath.length - 1];
      if (lastValuePath?.isPrimitive) {
        parentPath![lastValuePath?.listIndex!] = value;
      } else {
        parentPath![lastValuePath?.listIndex!][propertyName] = value;
      }
    } else {
      currentPath![propertyName] = value;
    }

    setFlowInputDataEditorState({ ...flowInputDataEditorState });
    setFlowInputLocalStorage(flowInputController, flowInputDataEditorState, flowInputDataEditorState.inputName!);
  };

  const getPathTestId = (valuePath: IValuePath[]) => {
    const idParts: string[] = [];
    valuePath.forEach((path: IValuePath) => {
      idParts.push(path.pathProperty);
    });
    return idParts.join(".");
  };

  const getFieldPair = (treeNode: TestDataNode) => {
    if (treeNode.schemaProperty!.isEmptyList) {
      return (
        <Form.FieldPair
          required={treeNode.schemaProperty!.required ? RequiredState.required : undefined}
          labelText={toLabel(treeNode.schemaProperty!.name)}
          className="flowinput-editor-form-fieldpair"
        >
          <div className="flowinput-editor-tree-empty">{treeNode.schemaProperty!.label}</div>
          {treeNode.schemaProperty?.name !== "" &&
          (treeNode.schemaProperty?.isEmptyList || treeNode.valuePath[treeNode.valuePath.length - 1]?.isList) ? (
            <div className="flowinput-editor-tree-button-fieldpair-container">
              <Button
                data-testid={
                  getPathTestId(treeNode.schemaProperty!.valuePath) +
                  "." +
                  treeNode.schemaProperty?.name +
                  ".add-button"
                }
                className="flowinput-editor-tree-add-remove-button"
                as={"button"}
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  addListItem(treeNode);
                }}
              >
                Add
              </Button>
              <Button
                data-testid={
                  getPathTestId(treeNode.schemaProperty!.valuePath) +
                  "." +
                  treeNode.schemaProperty?.name +
                  ".remove-button"
                }
                className="flowinput-editor-tree-add-remove-button"
                as={"button"}
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  removeListItem(treeNode);
                }}
              >
                Remove
              </Button>
            </div>
          ) : (
            <></>
          )}
        </Form.FieldPair>
      );
    }

    return (
      <Form.FieldPair
        required={treeNode.schemaProperty!.required ? RequiredState.required : undefined}
        labelText={treeNode.schemaProperty!.label}
        className="flowinput-editor-form-fieldpair"
      >
        {treeNode.schemaProperty!.schema === "INTEGER" ||
        treeNode.schemaProperty!.schema === "integer" ||
        treeNode.schemaProperty!.schema === "DECIMAL" ||
        treeNode.schemaProperty!.schema === "decimal" ? (
          <div>
            <NumericInput
              data-testid={getPathTestId(treeNode.schemaProperty!.valuePath) + "." + treeNode.schemaProperty?.name}
              name={treeNode.schemaProperty!.name}
              numericValue={getCurrentValue(treeNode.schemaProperty!) ?? ""}
              size={InputSize.medium}
              numericOptions={{
                format:
                  treeNode.schemaProperty!.schema === "INTEGER" || treeNode.schemaProperty!.schema === "integer"
                    ? "integer"
                    : "decimal"
              }}
              onValueChange={(newValue: number) => {
                onChange(treeNode.schemaProperty!.name, treeNode.schemaProperty!.valuePath, newValue);
              }}
            />
            <ValidationMessage message={treeNode.schemaProperty!.errorMessage} />
          </div>
        ) : (
          <div>
            <TextInput
              data-testid={getPathTestId(treeNode.schemaProperty!.valuePath) + "." + treeNode.schemaProperty?.name}
              className="flowinput-editor-textbox"
              value={getCurrentValue(treeNode.schemaProperty!) ?? ""}
              type="text"
              onChange={(e: React.FormEvent<HTMLInputElement>) => {
                const value = e.currentTarget.value;
                onChange(treeNode.schemaProperty!.name, treeNode.schemaProperty!.valuePath, value);
              }}
            />
            <ValidationMessage message={treeNode.schemaProperty!.errorMessage} />
          </div>
        )}
      </Form.FieldPair>
    );
  };

  let properties: INamedDocumentObjectSchemaProperty[];
  if (validationState.showing) {
    const [_missingInputs, inputProps] = validateRequiredProperties(
      reactFlowInstance.getNodes(),
      flowInputDataEditorState,
      flowInputController,
      flowInputDataEditorState.inputName!
    );
    properties = inputProps;
  } else {
    const defaultValue = getDefaultValueForFlowInput(flowInputDataEditorState?.inputName!, reactFlowInstance);
    loadOrInitFlowInput(
      flowInputController,
      flowInputDataEditorState,
      flowInputDataEditorState?.inputName!,
      defaultValue
    );
    properties = getFlowInputProperties(
      flowInputDataEditorState?.inputName!,
      flowInputDataEditorState?.excludedProperties,
      flowInputController,
      flowInputDataEditorState
    );
  }

  properties.forEach((flowInputProp: INamedDocumentObjectSchemaProperty) => {
    const defaultValue = getDefaultValueForFlowInput(flowInputDataEditorState?.inputName!, reactFlowInstance);
    initProperty(
      flowInputDataEditorState?.inputName!,
      flowInputController,
      flowInputDataEditorState,
      flowInputProp.name,
      flowInputProp.valuePath,
      defaultValue
    );
  });

  const getTreeData = () => {
    const treeData: TestDataNode[] = [];
    let largestTreeNodeId = 0;
    properties.forEach((property: INamedDocumentObjectSchemaProperty) => {
      let parentPathTreeNodeId: number | undefined = undefined;
      let parentPath: IValuePath | undefined = undefined;
      property.valuePath.forEach((currentPath: IValuePath, index: number) => {
        const listContainsPrimitive =
          currentPath.isPrimitive &&
          index === property.valuePath.length - 1 &&
          property.valuePath.length > 1 &&
          property.valuePath[property.valuePath.length - 2]?.isList;
        const itemName = currentPath?.listIndex !== undefined ? `Item ${currentPath.listIndex + 1}` : "";
        const nodeLabel = parentPath?.isList ? itemName : currentPath.pathProperty;
        let currentPathTreeNode: TestDataNode | undefined = treeData.find(
          (treeNode: TestDataNode) =>
            treeNode.parentId === parentPathTreeNodeId && treeNode.name === currentPath.pathProperty
        );
        if (!currentPathTreeNode) {
          currentPathTreeNode = {
            id: ++largestTreeNodeId,
            name: currentPath.pathProperty,
            label: nodeLabel,
            expanded: parentPath?.isList !== true || property.errorMessage !== undefined,
            valuePath: property.valuePath.slice(0, index + 1)
          };

          if (parentPathTreeNodeId) {
            currentPathTreeNode.parentId = parentPathTreeNodeId;
          }

          if (!listContainsPrimitive) {
            treeData.push(currentPathTreeNode);
          }
        }

        if (!listContainsPrimitive) {
          parentPathTreeNodeId = currentPathTreeNode.id;
        }

        parentPath = currentPath;
      });
      const propertyTreeNode: TestDataNode = {
        id: ++largestTreeNodeId,
        name: property.name,
        label: property.label ?? property.name,
        expanded: true,
        valuePath: property.valuePath,
        schemaProperty: property
      };
      if (parentPathTreeNodeId) {
        propertyTreeNode.parentId = parentPathTreeNodeId;
      }
      treeData.push(propertyTreeNode);
    });

    return treeData;
  };

  const treeData = getTreeData();

  const updateFlowInputData = () => {
    setFlowInputDataEditorState({ ...flowInputDataEditorState });
    setFlowInputLocalStorage(flowInputController, flowInputDataEditorState, flowInputDataEditorState.inputName!);

    const newProperties = getFlowInputProperties(
      flowInputDataEditorState?.inputName!,
      flowInputDataEditorState?.excludedProperties,
      flowInputController,
      flowInputDataEditorState
    );
    newProperties.forEach((flowInputProp: INamedDocumentObjectSchemaProperty) => {
      const defaultValue = getDefaultValueForFlowInput(flowInputDataEditorState?.inputName!, reactFlowInstance);
      initProperty(
        flowInputDataEditorState?.inputName!,
        flowInputController,
        flowInputDataEditorState,
        flowInputProp.name,
        flowInputProp.valuePath,
        defaultValue
      );
    });
  };

  const addListItem = (treeNode: TestDataNode) => {
    let valuePath = treeNode.valuePath;
    if (treeNode.valuePath[treeNode.valuePath.length - 1]?.pathProperty !== treeNode.name) {
      valuePath = treeNode.valuePath.concat([{ pathProperty: treeNode.name, isList: true }]);
    }
    const [currentPath, parentPath] = getValueAtPath(
      valuePath,
      flowInputDataEditorState?.testData[flowInputDataEditorState?.inputName!]
    );
    let newItemValue: any = null;
    if (treeNode.schemaProperty?.isEmptyList) {
      const treeNodeSchema = getSchema(treeNode.schemaProperty.schema);
      if (treeNodeSchema?.schemaType === DocumentSchemaType.object) {
        newItemValue = {};
      }
    }

    // typeof for arrays are "object"...
    if (
      Array.isArray(currentPath) &&
      currentPath.length > 0 &&
      currentPath[0] !== null &&
      !Array.isArray(currentPath[0]) &&
      typeof currentPath[0] === "object"
    ) {
      newItemValue = {};
    }
    if (currentPath == null && parentPath) {
      const lastValuePath = valuePath[valuePath.length - 1];
      if (lastValuePath?.listIndex) {
        parentPath[lastValuePath.listIndex] = [newItemValue];
      } else {
        parentPath[treeNode.name] = [newItemValue];
      }
    } else {
      currentPath.push(newItemValue);
    }
    updateFlowInputData();
  };

  const removeListItem = (treeNode: TestDataNode) => {
    let valuePath = treeNode.valuePath;
    if (treeNode.valuePath[treeNode.valuePath.length - 1]?.pathProperty !== treeNode.name) {
      valuePath = treeNode.valuePath.concat([{ pathProperty: treeNode.name, isList: true }]);
    }
    const [currentPath, _parentPath] = getValueAtPath(
      valuePath,
      flowInputDataEditorState?.testData[flowInputDataEditorState?.inputName!]
    );
    if (currentPath != null) {
      currentPath.pop();
    }
    updateFlowInputData();
  };

  return (
    <BannerProvider>
      <BannerConsumer>
        {(bctx: IBannerContext) => {
          return (
            <Form className="flowinput-editor-form">
              <Form.Header>
                <BannerPlaceholder style={{ marginTop: "20px", marginBottom: 0, marginLeft: 0, marginRight: 0 }} />
              </Form.Header>
              <Form.Body>
                <Form.Section title="Details">
                  <Form.Row className="flowinput-editor-form-input-row">
                    <Tree data={treeData.filter((x) => !x.parentId)} selectable={false} keySelector={(row) => row.id}>
                      {(node: TestDataNode) => {
                        const nodes = treeData.filter((x) => x.parentId === node.id);
                        const addRemoveButtons =
                          node.schemaProperty?.name !== "" &&
                          (node.schemaProperty?.isEmptyList || node.valuePath[node.valuePath.length - 1]?.isList);
                        if (nodes.length > 0) {
                          return (
                            <Tree.Branch
                              expanded={node.expanded}
                              className="flowinput-editor-tree-branch"
                              data={nodes}
                              data-testid={getPathTestId(node.valuePath) + ".container"}
                            >
                              {toLabel(node.label)}
                              {addRemoveButtons ? (
                                <div className="flowinput-editor-tree-button-container">
                                  <Button
                                    data-testid={getPathTestId(node.valuePath) + ".add-button"}
                                    className="flowinput-editor-tree-add-remove-button"
                                    as={"button"}
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      addListItem(node);
                                    }}
                                  >
                                    Add
                                  </Button>
                                  <Button
                                    data-testid={getPathTestId(node.valuePath) + ".remove-button"}
                                    className="flowinput-editor-tree-add-remove-button"
                                    as={"button"}
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      removeListItem(node);
                                    }}
                                  >
                                    Remove
                                  </Button>
                                </div>
                              ) : (
                                <></>
                              )}
                            </Tree.Branch>
                          );
                        }

                        return <Tree.Leaf>{node.schemaProperty ? getFieldPair(node) : <></>}</Tree.Leaf>;
                      }}
                    </Tree>
                  </Form.Row>
                  <Form.Row className="flowinput-editor-okbutton">
                    <Button
                      variance={"link"}
                      className="flowinput-editor-secondary-button"
                      as={"button"}
                      type="button"
                      onClick={() => closeNotifyModal(flowInputDataEditorState.inputName!)}
                    >
                      Cancel
                    </Button>
                    <Button
                      variance={"primary"}
                      className="flowinput-editor-secondary-button"
                      as={"button"}
                      type="button"
                      onClick={() => {
                        if (flowInputDataEditorState?.testData[flowInputDataEditorState?.inputName!]) {
                          flowInputDataEditorState.testData[flowInputDataEditorState.inputName!] = {};
                          updateFlowInputData();
                        }
                      }}
                    >
                      Reset
                    </Button>
                    <Button
                      data-testid="flowinput-editor-ok"
                      variance={"primary"}
                      as={"button"}
                      type="button"
                      onClick={() => closeNotifyModal(flowInputDataEditorState.inputName!, true, bctx)}
                    >
                      OK
                    </Button>
                  </Form.Row>
                </Form.Section>
              </Form.Body>
            </Form>
          );
        }}
      </BannerConsumer>
    </BannerProvider>
  );
};
